import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/services/api'
import type { User } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(localStorage.getItem('access_token'))
  const refreshToken = ref<string | null>(localStorage.getItem('refresh_token'))
  const isRefreshing = ref(false)
  const refreshPromise = ref<Promise<void> | null>(null)

  /**
   * Restauración de la sesión en curso.
   *
   * Vive fuera de un `ref` a propósito: es un mecanismo, no un estado que la
   * interfaz muestre, y con un `ref` cada lectura crearía una dependencia
   * que no aporta nada.
   */
  let restorePromise: Promise<void> | null = null

  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)
  const userRoles = computed(() => user.value?.roles ?? [])
  const userPermissions = computed(() => user.value?.permissions ?? [])

  const hasRole = (role: string): boolean => {
    return userRoles.value.includes(role)
  }

  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some(role => userRoles.value.includes(role))
  }

  const hasPermission = (permission: string): boolean => {
    return userPermissions.value.includes(permission)
  }

  const canAccessDashboard = computed(() => hasPermission('dashboard.access'))

  /**
   * Panel de marketing: lo ven el marketing y el admin.
   *
   * Se comprueba por permiso y no por rol porque el admin también entra, y
   * preguntar «¿es admin o marketing?» en cada sitio habría repetido la lista
   * de roles en todos lados.
   */
  const canAccessMarketing = computed(() => hasPermission('marketing.access'))

  async function login(username: string, password: string, rememberMe = true): Promise<void> {
    const response = await api.post('/auth/login', { username, password, remember_me: rememberMe })

    if (response.data.data?.access_token) {
      setTokens(response.data.data.access_token, response.data.data.refresh_token)
      await fetchUser()
      // Resetear la promesa de restauración para que la próxima llamada a initialize()
      // no devuelva la promesa antigua (que falló por no tener token). Ahora el token
      // existe y fetchUser() tendrá éxito inmediato si se vuelve a llamar.
      restorePromise = null
    }
  }

  async function fetchUser(): Promise<void> {
    try {
      const response = await api.get('/auth/me')
      user.value = response.data.data
    } catch (error) {
      // Solo se cierra la sesión si el servidor dice que el token ya no vale.
      // Con un error de red —el backend reiniciándose, sin cobertura— la sesión
      // sigue viva: tirar los tokens convertiría un problema pasajero en una
      // expulsión, y el usuario tendría que volver a escribir su contraseña.
      if (isAuthFailure(error)) {
        logout()
      }
      throw error
    }
  }

  /**
   * ¿El servidor ha rechazado la sesión, o simplemente no hemos podido hablar
   * con él?
   */
  function isAuthFailure(error: unknown): boolean {
    const status = (error as { response?: { status?: number } } | undefined)?.response?.status
    return status === 401 || status === 403
  }

  function setTokens(access: string, refresh: string): void {
    accessToken.value = access
    refreshToken.value = refresh
    localStorage.setItem('access_token', access)
    localStorage.setItem('refresh_token', refresh)
  }

  async function refreshAccessToken(): Promise<void> {
    if (isRefreshing.value && refreshPromise.value) {
      return refreshPromise.value
    }

    isRefreshing.value = true

    refreshPromise.value = (async () => {
      try {
        const response = await api.post('/auth/refresh', {}, {
          headers: {
            Authorization: `Bearer ${refreshToken.value}`,
          },
        })

        if (response.data.data?.access_token) {
          setTokens(response.data.data.access_token, response.data.data.refresh_token ?? refreshToken.value)
        }
      } catch (error) {
        logout()
        throw error
      } finally {
        isRefreshing.value = false
        refreshPromise.value = null
      }
    })()

    return refreshPromise.value
  }

  async function logout(): Promise<void> {
    // Primero avisamos al backend para que invalide el refresh token en su base
    // de datos. Hacemos la llamada **antes** de limpiar los tokens, así la
    // petición lleva el Authorization header y el backend puede invalidar la
    // sesión correctamente. Si falla, no importa: los tokens locales se
    // limpian igual y la sesión queda cerrada en el cliente.
    try {
      await api.post('/auth/logout')
    } catch {
      // Si el backend ya invalidó el token o no responde, no importa:
      // la sesión se cierra en el cliente igualmente.
    }

    // Ahora sí: limpiamos el estado local. El orden importa para que la
    // petición de logout salga con el Authorization header correcto.
    user.value = null
    accessToken.value = null
    refreshToken.value = null
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  }

  async function updateProfile(data: Partial<User>): Promise<void> {
    const response = await api.patch('/auth/profile', data)
    if (response.data.data) {
      user.value = { ...user.value, ...response.data.data }
    }
  }

  async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post('/auth/change-password', {
      current_password: currentPassword,
      password: newPassword,
      password_confirmation: newPassword,
    })
  }

  /**
   * Restaura la sesión guardada.
   *
   * Devuelve una promesa, y es lo que la guard de navegación espera antes de
   * decidir. Devolver `void` era lo que hacía que recargar la página sacara del
   * sistema: la primera navegación se resolvía con el store a medias, veía que
   * no había usuario y mandaba al login, sin llegar a tiempo a usar el token que
   * sí estaba guardado.
   *
   * Repetir la llamada no duplica la petición: la primera promesa se recuerda.
   */
  function initialize(): Promise<void> {
    if (restorePromise !== null) {
      return restorePromise
    }

    const storedAccess = localStorage.getItem('access_token')
    const storedRefresh = localStorage.getItem('refresh_token')

    if (storedAccess === null || storedRefresh === null) {
      restorePromise = Promise.resolve()
      return restorePromise
    }

    accessToken.value = storedAccess
    refreshToken.value = storedRefresh

    // El token no basta: `isAuthenticated` exige token **y** usuario, porque sin
    // usuario no hay permisos y las secciones se ocultan. Por eso hay que pedir
    // el perfil, y no solo leer los tokens del almacenamiento.
    restorePromise = fetchUser().catch(() => {
      // `fetchUser` ya ha limpiado la sesión si el token estaba caducado. Aquí no
      // hay nada que hacer: la promesa se resuelve igual para que la guard no
      // deje la navegación colgada.
    })

    return restorePromise
  }

  /**
   * Espera a que la sesión esté restaurada.
   *
   * Lo usa la guard de navegación. Va separada de `initialize` para que quede
   * claro cuál de las dos cosas se pide: arrancar la restauración, o simplemente
   * esperar a que termine.
   */
  function whenReady(): Promise<void> {
    return initialize()
  }

  return {
    user,
    accessToken,
    refreshToken,
    isRefreshing,
    isAuthenticated,
    userRoles,
    userPermissions,
    hasRole,
    hasAnyRole,
    hasPermission,
    canAccessDashboard,
    canAccessMarketing,
    login,
    fetchUser,
    setTokens,
    refreshAccessToken,
    logout,
    updateProfile,
    changePassword,
    initialize,
    whenReady,
  }
})

export type AuthStore = ReturnType<typeof useAuthStore>